import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { config } from '../config';
import { courseCategories } from '../data';

const LeadCaptureModal = ({ isOpen, onClose, selectedCourse = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: selectedCourse?.title || '',
    courseType: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    console.log('🚀 Form submission started');
    console.log('📡 API URL:', config.GOOGLE_APPS_SCRIPT_URL);
    console.log('📝 Form data:', formData);
    
    try {
      // Send data to Google Sheets via Apps Script
      const response = await fetch(config.GOOGLE_APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors', // Google Apps Script doesn't support CORS
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'enquiry',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          course: formData.course,
          courseType: formData.courseType,
          message: formData.message
        })
      });
      
      // Since no-cors mode is used, we can't read the response
      // We'll assume success if no error is thrown
      console.log('📨 Response received:', response);
      
      alert('Thank you for your interest! We will contact you within 24 hours.');
      onClose();
      setFormData({ name: '', email: '', phone: '', course: selectedCourse?.title || '', courseType: '', message: '' });
      setErrors({});
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.courseType) {
      newErrors.courseType = 'Course type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button - Top Right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 text-gray-600 hover:text-gray-800"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="text-center mb-6 pt-4 px-6">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-textPrimary mb-3">
              {selectedCourse ? `Enquire About ${selectedCourse.title}` : 'Start Your BIM Journey'}
            </h2>
            <p className="text-sm sm:text-base text-textSecondary">
              {selectedCourse 
                ? `Fill out the form below to get detailed information about ${selectedCourse.title}`
                : 'Fill out the form below and our team will get back to you with course details'
              }
            </p>
            {selectedCourse && (
              <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                <span className="text-sm text-primary font-medium">
                  Course: {selectedCourse.title}
                </span>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 px-6 pb-6">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-textPrimary mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-base ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-textPrimary mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-base ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-textPrimary mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-base ${errors.email ? 'border-red-500' : ''}`}
                placeholder="Enter your email address"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="course" className="block text-sm font-medium text-textPrimary mb-2">
                  Preferred Course
                </label>
                {selectedCourse ? (
                  <input
                    type="text"
                    value={formData.course}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 cursor-not-allowed text-base"
                    readOnly
                  />
                ) : (
                  <select
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-base"
                  >
                    <option value="">Select a course</option>
                    {courseCategories.map(category => (
                      <optgroup key={category.category} label={category.category}>
                        {category.courses.map(course => (
                          <option key={course.title} value={course.title}>
                            {course.title}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label htmlFor="courseType" className="block text-sm font-medium text-textPrimary mb-2">
                  Course Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="courseType"
                  name="courseType"
                  value={formData.courseType}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-base ${errors.courseType ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Select course type</option>
                  <option value="Full Time Course">Full Time Course (Regular Schedule)</option>
                  <option value="Online Flexible">Online Flexible (Self-paced Learning)</option>
                  <option value="Crash Course">Crash Course (Intensive Training)</option>
                  <option value="Weekend Batch">Weekend Batch (Saturday & Sunday)</option>
                </select>
                {errors.courseType && (
                  <p className="text-red-500 text-xs mt-1">{errors.courseType}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-textPrimary mb-2">
                Additional Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="3"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-base resize-none"
                placeholder="Any specific questions or requirements?"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full text-base sm:text-lg px-6 py-3"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>

          {/* Footer */}
          <div className="text-center px-6 pb-6">
            <p className="text-xs sm:text-sm text-textMuted">
              We'll contact you within 24 hours with course details and next steps
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadCaptureModal;
